/**
 * Subset of the service provider payload the KYC banner depends on.
 *
 * Structural on purpose: `shared-frontend` cannot import
 * `GetServiceProviderResult` from `@pflegenavi/service-provider-frontend/api`,
 * because that package already depends on this one.
 */
export interface KycBannerServiceProvider {
  payment_service_provider?: 'mangopay' | 'stripe' | null;
  mangopay_user_id?: string | null;
  mangopay_kyc_verified?: boolean;
}

/**
 * KYC is outstanding as soon as the MangoPay user exists — it does not depend on
 * the bank account, which providers can skip on web (ENG-2374).
 *
 * Shared by the web and mobile dashboard banners.
 */
export const shouldShowKycBanner = (
  data: KycBannerServiceProvider | undefined
): boolean =>
  data?.payment_service_provider === 'mangopay' &&
  Boolean(data?.mangopay_user_id) &&
  !data?.mangopay_kyc_verified;

export const KYC_RENEWAL_WARNING_DAYS = 90;
const DAY_MS = 24 * 60 * 60 * 1000;

/** Whole days until the renewal deadline, negative once it has passed. */
export const kycRenewalDaysLeft = (
  deadline: string | null | undefined,
  today: Date
): number | null => {
  if (!deadline) {
    return null;
  }
  const todayUtc = Date.UTC(
    today.getUTCFullYear(),
    today.getUTCMonth(),
    today.getUTCDate()
  );
  return Math.round((Date.parse(deadline) - todayUtc) / DAY_MS);
};

/** True within MangoPay's 90-day downgrade notice; a first KYC already sets a far deadline. */
export const isKycRenewalDue = (
  deadline: string | null | undefined,
  today: Date
): boolean => {
  const daysLeft = kycRenewalDaysLeft(deadline, today);
  return daysLeft !== null && daysLeft <= KYC_RENEWAL_WARNING_DAYS;
};

export interface KycWarningSubject {
  mangopay_kyc_verified?: boolean;
  kyc_renewal_deadline?: string | null;
}

export type KycWarning = 'not_verified' | 'renewal_due';

export const getKycWarning = (
  data: KycWarningSubject,
  today: Date
): KycWarning | null => {
  if (!data.mangopay_kyc_verified) {
    return 'not_verified';
  }
  return isKycRenewalDue(data.kyc_renewal_deadline, today)
    ? 'renewal_due'
    : null;
};

/** Backend 422 when a pay-in hits a legal entity that is not KYC-verified. */
export const isLegalEntityKycBlockingError = (error: unknown): boolean =>
  error instanceof Error &&
  error.message.includes('legal entity is not KYC-verified');
